
import React from 'react';
import SectionLayout from '@/components/SectionLayout';
import { useBreakpoint } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { AlertTriangle, CheckCircle } from 'lucide-react';

const TheWallSection = () => {
  const { isMobile } = useBreakpoint();

  const obstacles = [
    '"I need a professional landing page..."',
    '"I need compelling sales copy..."',
    '"I need a video sales letter..."',
    '"I need business card templates..."'
  ];

  return (
    <SectionLayout 
      backgroundColor="bg-gradient-to-br from-gray-50 via-white to-gray-100" 
      paddingY="py-12 md:py-16 lg:py-20 xl:py-24"
      containerWidth="wide"
      backgroundPattern="subtle-grid"
      patternColor="#003a70"
      patternOpacity={3}
    >
      <div className="text-center space-y-8 md:space-y-12 lg:space-y-16 px-4">
        
        {/* Section Header */}
        <div className="space-y-4 md:space-y-6 animate-fade-in">
          <div className="inline-flex items-center gap-2 md:gap-3 px-4 md:px-6 py-2 md:py-3 bg-gradient-to-r from-[#FEE6D8] to-[#FEE6D8]/80 rounded-full shadow-lg border-2 border-[#FEE6D8]/50">
            <AlertTriangle className="text-[#003a70]" size={isMobile ? 20 : 28} />
            <span className="text-[#003a70] font-bold text-sm md:text-lg lg:text-xl">The Reality Check</span>
          </div>
          
          <h2 className={cn(
            "font-bold text-[#003a70] leading-tight max-w-4xl mx-auto",
            isMobile ? "text-2xl" : "text-2xl md:text-3xl lg:text-4xl xl:text-5xl"
          )}>
            The Wall That Stops
            <br />
            <span className="bg-gradient-to-r from-[#003a70] to-[#003a70]/80 bg-clip-text text-transparent">Most Entrepreneurs</span>
          </h2>
        </div>

        {/* Story Introduction */}
        <div className="space-y-6 md:space-y-8 animate-fade-in" style={{ animationDelay: '0.2s' }}>
          <div className="bg-white/80 backdrop-blur-sm rounded-2xl p-6 md:p-8 lg:p-12 border border-gray-200/60 shadow-lg max-w-4xl mx-auto">
            <p className={cn(
              "text-[#003a70]/90 leading-relaxed",
              isMobile ? "text-base" : "text-lg md:text-xl lg:text-2xl"
            )}>
              Listen, I've seen this story play out <span className="font-bold text-[#003a70]">hundreds of times.</span>
            </p>
            
            <div className="space-y-3 md:space-y-4 mt-6 md:mt-8 text-left">
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-6 h-6 md:w-8 md:h-8 bg-[#FEE6D8] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 md:mt-1">
                  <CheckCircle className="text-[#003a70]" size={isMobile ? 12 : 16} />
                </div>
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-sm" : "text-base md:text-lg"
                )}>
                  Someone learns an amazing strategy. They get excited.
                </p>
              </div>
              
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-6 h-6 md:w-8 md:h-8 bg-[#FEE6D8] rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 md:mt-1">
                  <CheckCircle className="text-[#003a70]" size={isMobile ? 12 : 16} />
                </div>
                <p className={cn(
                  "text-[#003a70]/90 leading-relaxed",
                  isMobile ? "text-sm" : "text-base md:text-lg"
                )}>
                  They start planning.
                </p>
              </div>
              
              <div className="flex items-start gap-3 md:gap-4">
                <div className="w-6 h-6 md:w-8 md:h-8 bg-[#003a70]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 md:mt-1">
                  <AlertTriangle className="text-[#003a70]" size={isMobile ? 12 : 16} />
                </div>
                <p className={cn(
                  "text-[#003a70] font-bold leading-relaxed",
                  isMobile ? "text-sm" : "text-base md:text-lg"
                )}>
                  Then reality hits.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* The Wall - Obstacles */}
        <div className="space-y-6 md:space-y-8 animate-fade-in" style={{ animationDelay: '0.4s' }}>
          <div className="relative bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 lg:p-12 border-2 border-[#FEE6D8]/50 shadow-2xl max-w-4xl mx-auto overflow-hidden">
            {/* Subtle gradient overlay */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#FEE6D8]/20 via-transparent to-[#003a70]/5 pointer-events-none"></div>
            
            {/* Warning accent bar */}
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#003a70] to-[#003a70]/80"></div>
            
            <div className="relative z-10">
              <div className="space-y-4 md:space-y-6">
                {obstacles.map((obstacle, index) => (
                  <div 
                    key={index}
                    className="flex items-center gap-3 md:gap-4 p-4 md:p-6 bg-gradient-to-r from-[#FEE6D8]/30 to-[#FEE6D8]/20 rounded-xl border border-[#FEE6D8]/40 hover:shadow-lg transition-all duration-300"
                    style={{ animationDelay: `${0.1 * index}s` }}
                  >
                    <div className="text-xl md:text-2xl">👉</div>
                    <p className={cn(
                      "text-[#003a70] font-semibold leading-relaxed",
                      isMobile ? "text-sm" : "text-base md:text-lg lg:text-xl"
                    )}>
                      {obstacle}
                    </p>
                  </div>
                ))}
              </div>
              
              <div className="mt-8 md:mt-10 pt-6 md:pt-8 border-t border-[#FEE6D8]/50">
                <p className={cn(
                  "text-[#003a70] font-bold leading-relaxed",
                  isMobile ? "text-base" : "text-lg md:text-xl lg:text-2xl"
                )}>
                  And suddenly, what seemed simple becomes a months-long project.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Sound Familiar */}
        <div className="space-y-3 md:space-y-4 animate-fade-in" style={{ animationDelay: '0.6s' }}>
          <div className="bg-gradient-to-br from-[#FEE6D8]/20 via-white to-[#FEE6D8]/10 rounded-2xl p-6 md:p-8 lg:p-12 border border-[#FEE6D8]/30 shadow-lg max-w-3xl mx-auto">
            <h3 className={cn(
              "font-bold text-[#003a70] leading-tight",
              isMobile ? "text-xl" : "text-2xl md:text-3xl lg:text-4xl"
            )}>
              Sound familiar?
            </h3>
          </div>
        </div>

      </div>
    </SectionLayout>
  );
};

export default TheWallSection;
